import { useCallback, useEffect, useRef, useState } from "react";
import { api, resolveWsBackendOrigin } from "./api";
import { getSessionToken } from "./sessionToken";

export function useBoardSync(boardId, clientId, user) {
  const [elements, setElementsState] = useState([]);
  const [boardMeta, setBoardMeta] = useState(null);
  const [remoteCursors, setRemoteCursors] = useState({});
  const [connected, setConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const wsRef = useRef(null);
  const elementsRef = useRef([]);

  useEffect(() => {
    if (!boardId || !clientId) {
      setElementsState([]);
      elementsRef.current = [];
      setBoardMeta(null);
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const params = user?.role === "admin" ? { client_id: clientId } : {};
        const { data } = await api.get(`/portal/boards/${boardId}`, { params });
        if (!cancelled) {
          const els = data.elements || [];
          setElementsState(els);
          elementsRef.current = els;
          setBoardMeta(data);
        }
      } catch {
        if (!cancelled) {
          setElementsState([]);
          elementsRef.current = [];
          setBoardMeta(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [boardId, clientId, user?.role]);

  useEffect(() => {
    if (!boardId || loading) return undefined;
    // HTTP is same-origin via Vercel; WS still talks to Railway and needs Bearer.
    const httpOrigin = resolveWsBackendOrigin();
    const origin = httpOrigin.replace(/^http/i, "ws");
    const token = getSessionToken();
    const q = token ? `?token=${encodeURIComponent(token)}` : "";
    const ws = new WebSocket(`${origin}/api/ws/board/${encodeURIComponent(boardId)}${q}`);
    wsRef.current = ws;

    ws.onopen = () => setConnected(true);
    ws.onclose = () => setConnected(false);
    ws.onerror = () => setConnected(false);
    ws.onmessage = (event) => {
      let msg;
      try {
        msg = JSON.parse(event.data);
      } catch {
        return;
      }
      if (msg.type === "sync") {
        const els = msg.board?.elements || [];
        setElementsState(els);
        elementsRef.current = els;
        if (msg.board) setBoardMeta((prev) => ({ ...prev, ...msg.board }));
        const cursors = {};
        (msg.cursors || []).forEach((c) => {
          if (c?.user_id) cursors[c.user_id] = c;
        });
        setRemoteCursors(cursors);
        return;
      }
      if (msg.type === "elements") {
        const els = msg.elements || [];
        setElementsState(els);
        elementsRef.current = els;
        return;
      }
      if (msg.type === "cursor" && msg.cursor?.user_id) {
        setRemoteCursors((prev) => ({ ...prev, [msg.cursor.user_id]: msg.cursor }));
        return;
      }
      if (msg.type === "presence" && msg.action === "leave" && msg.user_id) {
        setRemoteCursors((prev) => {
          const next = { ...prev };
          delete next[msg.user_id];
          return next;
        });
      }
    };

    return () => {
      ws.close();
      wsRef.current = null;
      setConnected(false);
    };
  }, [boardId, loading]);

  const sendCursor = useCallback((x, y) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    ws.send(JSON.stringify({ type: "cursor", x, y }));
  }, []);

  const setElements = useCallback((next) => {
    const els = typeof next === "function" ? next(elementsRef.current) : next;
    elementsRef.current = els;
    setElementsState(els);
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: "elements", elements: els }));
    }
  }, []);

  return {
    elements,
    setElements,
    boardMeta,
    setBoardMeta,
    remoteCursors,
    connected,
    loading,
    sendCursor,
  };
}
